<template>
  <transition name="fade">
    <div class="fixed inset-0 z-50 flex items-center justify-center px-2">
      <div
        class="absolute inset-0 z-0 bg-black/20"
        v-on:keyup.enter="onConfirm()"
        v-on:click="onCancel()"
        tabindex="0"
      ></div>

      <div
        class="relative z-10 w-full max-w-5xl max-h-[95vh] bg-white rounded-2xl flex flex-col overflow-hidden"
        :class="{
          'max-w-lg': size === 'sm',
          'max-w-3xl': size === 'md',
          'max-w-5xl': size === 'lg',
        }"
      >
        <div class="overflow-y-auto flex-1">
        <!-- Stepper Header -->
          <div
            v-if="isStepper"
            class="flex items-start justify-between border-b border-gray-light px-6 py-6 pb-0"
          >
            <div class="flex flex-col gap-1">
              <p class="text-b2">{{ currentStep }} av {{ totalSteps }}</p>
              <h3 class="text-t6 font-bolder pb-2">{{ steps[currentStepIndex].title }}</h3>
            </div>
            <Button v-if="hasClose" variant="tertiary" @click="onCancel()" size="large">
              {{ currentStep == totalSteps ? '' : 'Avbryt'}}

              <template #icon-after>
                <IconClose />
              </template>
            </Button>
          </div>

          <div
            v-if="!isStepper && hasClose"
            class="flex items-end justify-end border-b border-gray-light px-6 py-6 pb-0"
          >
            <Button v-if="hasClose" variant="tertiary" @click="onCancel()" size="large">
              <template #icon-after>
                <IconClose />
              </template>
            </Button>
          </div>

          <!-- Regular Header -->
          <div
            v-else-if="(header || hasClose) && !hideHeaderButton"
            class="flex items-start justify-between px-6 py-6 pb-0"
            :class="headingLarge && !headingDivider ? '' : 'border-solid border-b border-gray-light pb-4'"
          >
            <h3 v-if="header" class="font-bolder" :class="headingLarge ? 'text-t5 text-center' : 'text-t6'">{{ header }}</h3>

            <Button v-if="hasClose" variant="tertiary" @click="onCancel()" size="large">
              {{ hideCloseText ? '' : 'Avbryt' }}
              <template #icon-after>
                <IconClose class="fill-turquoise-60" />
              </template>
            </Button>
          </div>

          <div
            v-else-if="hasCloseButton"
            class="flex flex-col items-end px-6 py-6 pb-0"
          >
            <Button variant="round" @click="onCancel()" size="large">
              <template #icon-after>
                <IconClose class="fill-turquoise-60" />
              </template>
            </Button>
          </div>

          <!-- Content -->
          <div :class="noMargin ? '' : ['px-6', headingLarge ? 'pt-4 pb-6' : 'py-6']">
            <slot v-if="!isStepper"></slot>
            <slot v-else :name="'step-' + currentStepIndex" :step="steps[currentStepIndex]"></slot>
          </div>

          <!-- Footer -->
          <div
            class="flex flex-col items-end justify-between gap-2 px-4 md:px-6"
            :class="[
              addSpace ? 'pb-16 pt-4' : 'pb-4 pt-2',
              !hideFooterDivider ? 'border-t border-solid border-gray-light md:py-4 md:pb-4' : ''
            ]"
          >
            <slot name="footer"></slot>
            <!-- Regular Footer -->
            <div v-if="!isStepper" class="flex flex-1 justify-end gap-2">
              <Button
                v-if="cancelText"
                :variant="secondaryButtonStyle"
                v-on:click="onCancel()"
                @keyup.enter="onCancel()"
                tabindex="0"
                :disabled="loading"
                size="large"
              >
                {{ cancelText }}
              </Button>
              <Button
                v-if="confirmText"
                variant="primary"
                v-on:click="onConfirm()"
                @keyup.enter="onConfirm()"
                tabindex="0"
                 size="large"
                :disabled="disabled"
                :loadingText="loadingText ?? 'Skickar'"
                :loading="loading ?? isLoading"
              >
                {{ confirmText }}
              </Button>
            </div>
            <!-- Stepper Footer -->
            <div v-else class="flex flex-1 justify-end gap-2">
              <Button
                v-if="currentStepIndex > 0 && !isLastStep"
                :variant="secondaryButtonStyle"
                v-on:click="previousStep"
                 size="large"
                tabindex="0"
              >
                {{ steps[currentStepIndex].backButtonText || 'Tillbaka' }}
              </Button>

              <Button
                variant="primary"
                :href="computedHref"
                @click="handleButtonClick"
                tabindex="0"
                 size="large"
                :loading="isLoading"
                loadingText="Skickar"
              >
                {{
                  isLastStep
                    ? steps[currentStepIndex].confirmButtonText || 'Gå till checklistan'
                    : steps[currentStepIndex].nextButtonText || 'Nästa'
                }}
              </Button>
            </div>

            <p class="text-danger">
              {{
                isError ?
                  "Något gick fel, försök igen"
                  : ""
              }}
            </p>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import Button from '@/components/ui/button';
import IconClose from '@/components/icons/IconClose';

export default {
  name: 'Modal',
  components: {
    Button,
    IconClose,
  },
  data() {
    return {
      currentStepIndex: 0,
      isLoading: false,
      isError: false,
    };
  },
  props: {
    noMargin: {
      type: Boolean,
      default: false
    },
    hideCloseText: {
      type: Boolean,
      default: false
    },
    hideHeaderButton: {
      type: Boolean,
      default: false
    },
    secondaryButtonStyle: {
      type: String,
      default: "secondary"
    },
    headingLarge: {
      type: Boolean,
      default: false
    },
    headingDivider: {
      type: Boolean,
      default: false
    },
    hideFooterDivider: {
      type: Boolean,
      default: false
    },
    sendEmail: {
      type: Function,
      required: false
    },
    confirmText: {
      type: String,
    },
    cancelText: {
      type: String,
    },
    loadingText: {
      type: String,
    },
    loading: {
      type: Boolean,
    },
    disabled: {
      type: Boolean,
    },
    addSpace: {
      type: Boolean,
      default: true
    },
    size: {
      type: String,
      validator: function (value) {
        return ['sm', 'md', 'lg'].includes(value);
      },
      default: 'md',
    },
    closeable: {
      type: Boolean,
      default: true,
    },
    header: {
      type: String,
      default: '',
    },
    hasClose: {
      type: Boolean,
      default: false,
    },
    hasCloseButton: {
      type: Boolean,
      default: false,
    },
    isStepper: {
      type: Boolean,
      default: false,
    },
    objectType: {
      type: String,
      default: ""
    },
    steps: {
      type: Array,
      default: () => [],
      validator: function (value) {
        return value.every((step) => {
          // Validate required title and optional button texts
          const hasTitle = typeof step.title === 'string';
          const hasValidButtonTexts =
            (!step.nextButtonText || typeof step.nextButtonText === 'string') &&
            (!step.backButtonText || typeof step.backButtonText === 'string') &&
            (!step.confirmButtonText || typeof step.confirmButtonText === 'string');
          return hasTitle && hasValidButtonTexts;
        });
      },
    },
  },
  computed: {
    currentStep() {
      return this.currentStepIndex + 1;
    },
    totalSteps() {
      return this.steps.length;
    },
    isLastStep() {
      return this.currentStepIndex === this.totalSteps - 1;
    },
    isSigningStep() {
      return this.currentStepIndex === this.totalSteps - 2;
    },
    computedHref() {
      if (this.isSigningStep) return null;
      return this.isLastStep ? this.getHrefForObjectType(this.objectType) : null;
    }
  },
  methods: {
    async handleButtonClick(event) {
      if (this.isSigningStep) {
        this.isError = false;
        this.isLoading = true;
        event.preventDefault(); // Stop any default navigation

        const result = await this.sendEmail();
//        const result = await this.$emit('sendEmailAndWait');

        if (result === 'success') {
          this.isLoading = false;
          this.nextStep();
        } else {
          // Handle error if needed
          this.isLoading = false;
          this.isError = true;
          console.error('Email send failed or was cancelled');
        }
      } else if (!this.isLastStep) {
        this.nextStep();
      }
    },

    getHrefForObjectType(type) {
      switch (type) {
        case 'premise':
          return '/parkering-fordonsplatser/saga-upp-fordonsplats/';
        case 'parkering':
          return '/parkering-fordonsplatser/saga-upp-fordonsplats/';
        default:
          return '/din-bostad/saga-upp-din-bostad/';
      }
    },

    onConfirm() {
      document.removeEventListener('keyup', this._keyUpListeners);
      document.querySelector('html').classList.remove('modal-open');
      this.$emit('confirm');
    },
    onCancel() {
      document.removeEventListener('keyup', this._keyUpListeners);
      document.querySelector('html').classList.remove('modal-open');
      this.$emit('cancel');
    },
    nextStep() {
      if (this.currentStepIndex < this.totalSteps - 1) {
        this.currentStepIndex++;
        this.$emit('step-change', this.currentStepIndex);
      }
    },
    previousStep() {
      if (this.currentStepIndex > 0) {
        this.currentStepIndex--;
        this.$emit('step-change', this.currentStepIndex);
      }
    },
    _keyUpListeners: function (e) {
      if (e.keyCode == 13 && !this.isStepper) this.onConfirm();
      if (e.keyCode == 27) this.onCancel();
    },
  },
  created() {
    document.addEventListener('keyup', this._keyUpListeners);
  },
  mounted() {
    // Lock background scroll
    const scrollBarWidth = window.innerWidth - document.documentElement.clientWidth;
    document.body.style.overflow = 'hidden';
    document.body.style.paddingRight = `${scrollBarWidth}px`;
  },
  unmounted() {
    // Restore scroll when modal is removed
    document.body.style.overflow = '';
    document.body.style.paddingRight = '';
  },
};
</script>
